import React, { SetStateAction, useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Grid,
  Typography,
  Paper,
  Stack,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import LinkIcon from "@mui/icons-material/Link";
import SearchIcon from "@mui/icons-material/Search";
import { isWebsiteAddress } from "../../../Shared-ui/utils/Common/Common";
import { useDispatch } from "react-redux";
import {
  StartLoading,
  StopLoading,
} from "../../../Shared-ui/Store/LoadingReducer/LoaderActions";
import BaseInstance from "../../../Services/AxiosInstance/BaseInstance";
import {
  DISCOVER_URLS,
  KNOWLEDGE,
  KNOWLEDGE_BASE,
  URLS_TASK_STATUS,
} from "../../../Services/Endpoint";
import { ShowError } from "../../../Shared-ui/Store/MessageReducer/MsgActions";
import { KnowledgeBase, KnowledgeWebsite } from "../../../Shared-ui/Types/KnowledgeBase";

interface MyComponentProps {
  setKnowledgeBaseWebsite: React.Dispatch<SetStateAction<KnowledgeWebsite>>;
  knowledgeBase: KnowledgeBase
}

const WebsiteTab: React.FC<MyComponentProps> = (props) => {
  const websiteInitial = {
    base_url: "",
    max_depth: 2,
    max_links: 50,
    include_external: false,
  };
  const [website, setWebsite] = useState(websiteInitial);
  const [websiteUrlErr, setWebsiteUrlErr] = useState({
    error: "",
    value: false,
  });
  const [scannedUrls, setScannedUrls] = useState<{ url: string }[]>([]);
  const [selectedUrls, setSelectedUrls] = useState<string[]>([]);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [taskMessage, setTaskMessage] = useState<string>("");
  const [knowledgeBase, setKnowledgeBase] = useState();

  const dispatch = useDispatch();


  const getKnowledgeBase = async () => {
    try {
      dispatch(StartLoading());
      const data = await BaseInstance.get(`${KNOWLEDGE_BASE}/${props.knowledgeBase.id}`).then(
        (res) => res?.data
      );
      if (data.documents.length > 0) {
        if (data.documents[0].document_metadata && data.documents[0].document_metadata.domain) {
          setWebsite(preVal => ({ ...preVal, base_url: `https://${data.documents[0].document_metadata.domain}` }))
        }
      }
      setKnowledgeBase(data)
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.statusText));
    } finally {
      dispatch(StopLoading());
    }
  }
  useEffect(() => {

    getKnowledgeBase()
  }, [])


  useEffect(() => {
    if (!taskId) return;
    dispatch(StartLoading());
    let interval: NodeJS.Timeout;

    // Poll every 2 seconds
    interval = setInterval(async () => {
      const data: { status?: string } | undefined = await getTaskStatus();
      // If task is completed, stop polling
      if (data?.status === "COMPLETED") {
        setTaskMessage("");
        dispatch(StopLoading());
        clearInterval(interval);
      }
    }, 2000);

    // Cleanup on unmount or when taskId changes
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  useEffect(() => {
    props.setKnowledgeBaseWebsite({
      max_depth: website.max_depth,
      max_links: website.max_links,
      urls: [...selectedUrls],
    });
  }, [selectedUrls]);

  const getTaskStatus = async () => {
    try {
      const data = await BaseInstance.get(
        `${URLS_TASK_STATUS}/${taskId}/status`
      ).then((res) => res?.data);
      if (
        data.status === "COMPLETED" &&
        data.result &&
        data.result.discovered_urls
      ) {
        setScannedUrls(data.result.discovered_urls || []);
        console.log("Discovered URLs:", data.result.discovered_urls);
        setSelectedUrls([]);
      }
      return data;
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.statusText));
      dispatch(StopLoading());
    } finally {
    }
  };

  const getScanUrls = async () => {
    try {
      dispatch(StartLoading());
      const data = await BaseInstance.post(
        `${DISCOVER_URLS}?background=true`,
        website
      ).then((res) => res?.data);
      if (data?.task_id) {
        setTaskId(data.task_id);
      } else {
        dispatch(ShowError("No task ID returned from the server."));
      }
      if (data.message && data.estimated_time) {
        setTaskMessage(`${data?.message} ${data?.estimated_time}`);
      } else {
        setTaskMessage("No task information available.");
      }
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.statusText));
    } finally {
      dispatch(StopLoading());
    }
  };

  const scanUrl = () => {
    if (!isWebsiteAddress(website.base_url)) {
      setWebsiteUrlErr({ error: "url format not valid", value: true });
    } else {
      setWebsiteUrlErr({ error: "", value: false });
      getScanUrls();
    }
  };
  const handleSelectUrl = (url: string) => {
    setSelectedUrls((prev) =>
      prev.includes(url) ? prev.filter((u) => u !== url) : [...prev, url]
    );
    props.setKnowledgeBaseWebsite({
      max_depth: website.max_depth,
      max_links: website.max_links,
      urls: [...selectedUrls],
    });
  };

  const handleSelectAll = () => {
    setSelectedUrls(scannedUrls.map((u) => u.url));
    props.setKnowledgeBaseWebsite({
      max_depth: website.max_depth,
      max_links: website.max_links,
      urls: [...selectedUrls],
    });
  };
  const handleDeselectAll = () => setSelectedUrls([]);

  return (
    <Box sx={{ mt: 2 }}>
      <Box>
        {/* Base Website URL */}
        <Box sx={{ mb: 2 }}>
          <FormControl fullWidth variant="outlined" error={websiteUrlErr.value}>
            <InputLabel
              shrink
              htmlFor="website-url"
              sx={{ background: "#fff", px: 0.5 }}
            >
              Base Website URL *
            </InputLabel>
            <TextField
              required
              name="base_url"
              value={website.base_url}
              onChange={(e) =>
                setWebsite({ ...website, base_url: e.target.value })
              }
              id="website-url"
              placeholder="https://example.com"
              variant="outlined"
              fullWidth
              sx={{ minHeight: "56px" }}
              error={websiteUrlErr.value}
              helperText={websiteUrlErr.error}
            />
          </FormControl>
        </Box>
        <Grid
          item
          xs={12}
          md={4}
          sx={{ display: "flex", alignItems: "flex-end" }}
        >
          <Button
            variant="contained"
            disableElevation
            size="large"
            sx={{
              textTransform: "capitalize",
              height: "56px",
              width: "100%",
            }}
            startIcon={<SearchIcon />}
            onClick={scanUrl}
          >
            Scan URLs
          </Button>
        </Grid>

        {/* Maximum Depth and Maximum Links */}
        <Box
          sx={{
            width: "100%",
            display: "flex",
            gap: 2,
            mt: 2,
          }}
        >
          <Box
            sx={{
              width: "100%",
              display: "flex",
            }}
          >
            <TextField
              label="Maximum Depth"
              type="number"
              value={website.max_depth}
              onChange={(e) => {
                const value = Math.min(10, Math.max(2, parseInt(e.target.value) || 2));
                setWebsite({ ...website, max_depth: value });
              }}
              onKeyPress={(e) => {
                if (!/[0-9]/.test(e.key)) {
                  e.preventDefault();
                }
              }}
              fullWidth
              InputProps={{
                inputProps: {
                  min: 1,
                  max: 10,
                },
              }}
              helperText="Maximum depth to crawl (default: 2, max: 10)"
              sx={{
                "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                {
                  "-webkit-appearance": "none",
                  margin: 0,
                },
                "& input[type=number]": {
                  "-moz-appearance": "textfield",
                },
              }}
            />
          </Box>
          <Box
            sx={{
              width: "100%",
              display: "flex",
            }}
          >
            <TextField
              label="Maximum Links"
              type="number"
              value={website.max_links}
              onChange={(e) => {
                const value = Math.max(1, parseInt(e.target.value) || 1);
                setWebsite({ ...website, max_links: value });
              }}
              onKeyPress={(e) => {
                if (!/[0-9]/.test(e.key)) {
                  e.preventDefault();
                }
              }}
              fullWidth
              InputProps={{
                inputProps: {
                  min: 1,
                  max: 100,
                },
              }}
              helperText="Number of links to crawl (default: 50)"
              sx={{
                "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                {
                  "-webkit-appearance": "none",
                  margin: 0,
                },
                "& input[type=number]": {
                  "-moz-appearance": "textfield",
                },
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Task Status Message */}
      {taskMessage && (
        <Box sx={{ mt: 2, mb: 2 }}>
          <Typography variant="body2" color="textSecondary">
            {taskMessage}
          </Typography>
        </Box>
      )}

      {/* URLs List with checkboxes */}
      {scannedUrls.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography fontWeight="bold" sx={{ mb: 1 }}>
            Select URLs ({selectedUrls.length} of {scannedUrls.length} selected)
          </Typography>
          <Box sx={{ display: "flex", gap: 2, mb: 1 }}>
            <Button variant="outlined" size="small" onClick={handleSelectAll}>
              Select All
            </Button>
            <Button variant="outlined" size="small" onClick={handleDeselectAll}>
              Deselect All
            </Button>
          </Box>
          <Paper
            variant="outlined"
            sx={{ maxHeight: 220, overflowY: "auto", mb: 2 }}
          >
            <Stack>
              {scannedUrls.map((url: { url: string }) => (
                <FormControlLabel
                  key={url.url}
                  control={
                    <Checkbox
                      checked={selectedUrls.includes(url.url)}
                      onChange={() => handleSelectUrl(url.url)}
                    />
                  }
                  label={url.url}
                  sx={{ ml: 1 }}
                />
              ))}
            </Stack>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default WebsiteTab;
